// @cntrl/realtime — the ONE SignalR hub connection per app (core only; Antenna opens no sockets).
// The connection manager:
//   • Auth: mint a one-time short-TTL WS TICKET via authenticated REST (POST /realtime/ticket), present it
//     on connect — NEVER the session bearer in a URL (the MVP's ?token= handshake is a named defect).
//   • Reconnect: the MVP's proven backoff (retry-policy.ts) + the "reset only after stable > 15s" storm guard.
//   • Catch-up: every (re)connect fires `onConnected` → the app invalidates every subscribed scope and
//     REST-refetches UNCONDITIONALLY (GATE-4). The server never replays; the per-entity `v` on each signal
//     still guards against a stale signal clobbering a fresher refetch.
//   • Subscriptions are declarative: joinGroup/leaveGroup mirror route mounts; join authorization is server-side.
import {
  type HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import type { UiSignal } from "@cntrl/api-client";
import { BACKOFF_MS, CntrlRetryPolicy } from "./retry-policy.js";

/** How long a connection must hold before the backoff is considered "reset-worthy" (storm guard).*/
const STABLE_THRESHOLD_MS = 15_000;

export interface RealtimeClientOptions {
  /** The realtime base, e.g. `${WS_URL}/ws/live` — same-origin `/ws/live` in the one-host scheme.*/
  hubUrl: string;
  /** Mint a fresh one-time WS ticket via authenticated REST (realtime/ticket). Called on every connect.*/
  mintTicket: () => Promise<string>;
  /** Fired on first connect AND every reconnect — the app refetches every subscribed scope unconditionally. */
  onConnected: () => void;
  /** Fired for each plane-B signal `{ type, id, v, t }`. The invalidation registry lives in the app.*/
  onSignal: (signal: UiSignal) => void;
  /** Optional: surface connection-state changes to the app (offline banner, etc.). */
  onStateChange?: (state: HubConnectionState) => void;
}

/**
 * Owns the single hub connection. Route components call {@link joinGroup}/{@link leaveGroup}; the shell owns
 * start/stop. The bearer is read through TokenProvider by the caller supplying {@link RealtimeClientOptions.mintTicket}.
 */
export class RealtimeClient {
  private connection: HubConnection | null = null;
  private readonly joined = new Set<string>();
  private readonly retryPolicy = new CntrlRetryPolicy();
  /** When the current connection was (re)established — drives the "reset backoff only after stable >15s" guard. */
  private connectionEstablishedAt = 0;
  /** Initial-connect retry state — `withAutomaticReconnect` does NOT retry a failed FIRST connect. */
  private initialAttempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryResolve: (() => void) | null = null;
  /** True while a start() is owned by connectInitial's retry loop OR reconnectNow — the single-start mutex that
   * keeps a foreground reconnectNow from calling start concurrently with the initial-connect loop.*/
  private connecting = false;

  constructor(private readonly options: RealtimeClientOptions) {}

  /** Build + start the connection. Idempotent — a second call while connected is a no-op. */
  async start(): Promise<void> {
    if (this.connection && this.connection.state !== HubConnectionState.Disconnected) return;

    const connection = new HubConnectionBuilder()
      .withUrl(this.options.hubUrl, {
        // The ticket is fetched fresh for every (re)connect via accessTokenFactory — SignalR calls it on
        // each negotiation, so a single-use ticket is never reused.
        accessTokenFactory: () => this.options.mintTicket(),
      })
      .withAutomaticReconnect(this.retryPolicy)
      // Keepalive undercuts the ALB idle timeout. SignalR's keepalive subsumes the MVP's 25-30s ping.
      .withKeepAliveInterval(15_000)
      .withServerTimeout(60_000)
      .configureLogging(LogLevel.Warning)
      .build();

    connection.onreconnected(() => {
      this.connectionEstablishedAt = Date.now();
      void this.handleConnected();
    });
    connection.onclose(() => this.options.onStateChange?.(HubConnectionState.Disconnected));
    connection.onreconnecting(() => this.onDisconnected());

    // Server → client signal frames: a single "signal" method carries the {type,id,v,t} payload.
    connection.on("signal", (signal: UiSignal) => this.options.onSignal(signal));

    this.connection = connection;
    await this.connectInitial();
  }

  /**
   * The FIRST connect is not covered by `withAutomaticReconnect` — that only recovers a connection that was
   * already established and then dropped. So retry the initial `start()` on the same backoff, never giving up:
   * a transient network blip, or a 5xx from the WS-ticket mint inside `accessTokenFactory`, at first connect
   * must not leave the session with no live updates (the guest-scan door-tab case). {@link stop} cancels an
   * in-flight retry.
   */
  private async connectInitial(): Promise<void> {
    // Hold the single-start mutex for the whole retry loop so a foreground reconnectNow() during a backoff
    // sleep can't call start() on the same connection concurrently (which would throw + zombie-loop).
    this.connecting = true;
    try {
      while (this.connection) {
        try {
          await this.connection.start();
          this.initialAttempt = 0;
          this.connectionEstablishedAt = Date.now();
          this.retryPolicy.reset(); // a fresh connection starts the backoff sequence at 1s
          await this.handleConnected();
          return;
        } catch {
          if (!this.connection) return; // stopped mid-attempt
          const wait = BACKOFF_MS[Math.min(this.initialAttempt, BACKOFF_MS.length - 1)];
          this.initialAttempt += 1;
          await this.sleep(wait);
          if (!this.connection) return; // stopped during the backoff
        }
      }
    } finally {
      this.connecting = false;
    }
  }

  /** A cancellable backoff sleep — {@link stop} resolves it early so a teardown never waits out the delay. */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.retryResolve = resolve;
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        this.retryResolve = null;
        resolve();
      }, ms);
    });
  }

  /**
   * The connection just dropped (onreconnecting) — the storm guard: reset the backoff to 1s ONLY
   * if the connection had held stable ≥ 15s; a flap (< 15s) keeps the counter climbing toward the 30s cap,
   * so a reconnect storm doesn't restart at 1s every time.
   */
  private onDisconnected(): void {
    const heldMs = Date.now() - this.connectionEstablishedAt;
    if (heldMs >= STABLE_THRESHOLD_MS) {
      this.retryPolicy.reset();
    }
    this.options.onStateChange?.(HubConnectionState.Reconnecting);
  }

  /** Rejoin the tracked groups and fire the unconditional-refetch catch-up (GATE-4). */
  private async handleConnected(): Promise<void> {
    this.options.onStateChange?.(HubConnectionState.Connected);

    // Re-join every declaratively-tracked group; authorization is re-checked server-side per join.
    for (const topic of this.joined) {
      await this.invokeJoin(topic);
    }
    this.options.onConnected();
  }

  /** Join a scope group (e.g. `show:{id}`, `band:{id}`, `user:{id}`, `org:{id}`). Tracked for reconnect rejoin. */
  async joinGroup(topic: string): Promise<void> {
    this.joined.add(topic);
    if (this.connection?.state === HubConnectionState.Connected) {
      await this.invokeJoin(topic);
    }
  }

  /** Leave a scope group and stop tracking it. */
  async leaveGroup(topic: string): Promise<void> {
    this.joined.delete(topic);
    if (this.connection?.state === HubConnectionState.Connected) {
      await this.connection.invoke("Leave", topic);
    }
  }

  private async invokeJoin(topic: string): Promise<void> {
    if (this.connection?.state === HubConnectionState.Connected) {
      await this.connection.invoke("Join", topic);
    }
  }

  /**
   * Foreground nudge (called on `visibilitychange` / Capacitor `appStateChange→active` / bfcache `pageshow`,
   * ) — iOS suspends the webview so sockets die by design. `withAutomaticReconnect` owns a *Reconnecting*
   * socket (it retries when JS resumes on foreground, firing `onreconnected`→`handleConnected`→refetch); this
   * only force-starts a fully *Disconnected* socket (the "gave up / never started" edge). Two guards make it
   * safe as the first production caller: (1) `connecting` serialises against connectInitial's retry loop and
   * against a concurrent reconnectNow, so start() is never called twice at once (which throws + zombie-loops);
   * (2) the try/catch keeps a failed foreground start() from surfacing as an unhandled rejection.
   */
  async reconnectNow(): Promise<void> {
    if (!this.connection || this.connecting) return;
    if (this.connection.state !== HubConnectionState.Disconnected) return;
    this.connecting = true;
    try {
      await this.connection.start();
      this.connectionEstablishedAt = Date.now();
      this.retryPolicy.reset();
      await this.handleConnected();
    } catch {
      // a failed forced start leaves the socket Disconnected; the next foreground event retries.
    } finally {
      this.connecting = false;
    }
  }

  /** Leave all groups and stop. Used by switchBand scorch and logout.*/
  async stop(): Promise<void> {
    // Cancel any in-flight initial-connect backoff so teardown is immediate, and null the connection FIRST so
    // the retry loop (which guards on `this.connection`) exits on its next check rather than reconnecting.
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.retryResolve?.();
    this.retryResolve = null;
    this.joined.clear();
    const connection = this.connection;
    this.connection = null;
    if (connection) {
      await connection.stop();
    }
  }

  get state(): HubConnectionState {
    return this.connection?.state ?? HubConnectionState.Disconnected;
  }
}
